/**
 * payMethod — CLIENT MIRROR of netlify/functions/_lib/pay-method.ts.
 *
 * The client can't import a Netlify function lib, and `tests/pay-method.test.ts`
 * FAILS if the two ever disagree — a section whose value the two halves spell
 * differently is a section the portal shows one way and bills another.
 *
 * See the server file for why `ach` (we pull) and `ach_push` (they send) are
 * deliberately different values.
 */

export const PAY_METHODS = ['card', 'ach', 'check', 'ach_push'] as const;
export type PayMethod = (typeof PAY_METHODS)[number];

/** We take the money. Requires a saved method in the Stripe wallet. */
export const PULL_METHODS = ['card', 'ach'] as const;
/** They send the money. Requires nothing saved. */
export const PUSH_METHODS = ['check', 'ach_push'] as const;

export function isPayMethod(v: unknown): v is PayMethod {
  return typeof v === 'string' && (PAY_METHODS as readonly string[]).includes(v);
}
export function isPushMethod(v: unknown): boolean {
  return typeof v === 'string' && (PUSH_METHODS as readonly string[]).includes(v);
}
export function isPullMethod(v: unknown): boolean {
  return typeof v === 'string' && (PULL_METHODS as readonly string[]).includes(v);
}

/** The customer-facing sections, in the order they are shown. */
export const PAY_SECTIONS = ['equipment', 'cylinder', 'product'] as const;
export type PaySection = (typeof PAY_SECTIONS)[number];

/**
 * Section → the `orders.customers` column. ⚠ `equipment` maps to `_rentals`
 * and `cylinder` to `_tanks`: the column names came first and do not read
 * like the section names, which is precisely why nobody should re-derive this
 * mapping inline.
 */
export const SECTION_COLUMN: Record<PaySection, string> = {
  equipment: 'payment_method_rentals',
  cylinder: 'payment_method_tanks',
  product: 'payment_method_orders',
};

export function isPaySection(v: unknown): v is PaySection {
  return typeof v === 'string' && (PAY_SECTIONS as readonly string[]).includes(v);
}

/** What a human is told this value means. */
export const PAY_METHOD_LABEL: Record<PayMethod, string> = {
  card: 'We charge your saved card',
  ach: 'We debit your saved bank account',
  check: "I'll mail checks",
  ach_push: "I'll send ACH",
};
